import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BookOpen, Calendar, ChevronDown, ChevronRight, FileText, Info, Ruler, Settings2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EditorToolbar, SectionEditor } from './SectionEditor';
import { useVolumeMetadata } from './useVolumeMetadata';
import { VolumeMetadataPanel } from './VolumeMetadataPanel';
import { useOutlineSections, useUpdateOutlineSection } from './workingOutlineHooks';
// Common fonts for proposals.
const FONT_OPTIONS = [
    {
        label: 'Times New Roman',
        value: 'Times New Roman, serif'
    },
    {
        label: 'Arial',
        value: 'Arial, sans-serif'
    },
    {
        label: 'Calibri',
        value: 'Calibri, sans-serif'
    },
    {
        label: 'Georgia',
        value: 'Georgia, serif'
    },
    {
        label: 'Courier New',
        value: 'Courier New, monospace'
    },
    {
        label: 'Garamond',
        value: 'Garamond, serif'
    },
    {
        label: 'Cambria',
        value: 'Cambria, serif'
    },
    {
        label: 'Verdana',
        value: 'Verdana, sans-serif'
    },
    {
        label: 'Trebuchet MS',
        value: 'Trebuchet MS, sans-serif'
    },
    {
        label: 'Palatino',
        value: 'Palatino, serif'
    }
];
/**
 * SectionPadShell — continuous document editor for prose volumes.
 *
 * Renders all sections as one flowing "page" (Google Docs / Notion feel).
 * The editing surface is seamless — no per-section card borders. A shared
 * sticky toolbar appears when any section is focused. Format presets
 * (font, margins, heading sizes) come from solicitation format rules and
 * can be overridden via a settings popover.
 *
 * The TOC highlights the section currently in view via IntersectionObserver.
 */ export function SectionPadShell({ projectId, volume, sectionId }) {
    const navigate = useNavigate();
    const { id, volumeId } = useParams();
    const outline = useOutlineSections(projectId, volumeId);
    const updateSection = useUpdateOutlineSection(projectId, volumeId);
    const meta = useVolumeMetadata(projectId, volume);
    const sections = useMemo(()=>flattenAll(outline.data ?? []), [
        outline.data
    ]);
    const [activeEditor, setActiveEditor] = useState(null);
    const [formatOverrides, setFormatOverrides] = useState({});
    const [showSettings, setShowSettings] = useState(false);
    const scrollContainerRef = useRef(null);
    const pageRef = useRef(null);
    const [pageCount, setPageCount] = useState(1);
    const baseStyle = useMemo(()=>deriveFormatStyle(meta), [
        meta
    ]);
    const formatStyle = useMemo(()=>({
            ...baseStyle,
            ...formatOverrides
        }), [
        baseStyle,
        formatOverrides
    ]);
    // --- Scroll-synced TOC: update URL when user scrolls past section headings ---
    const sectionRefs = useRef(new Map());
    const scrollNavigation = useRef(null);
    const registerRef = useCallback((sectionId, el)=>{
        if (el) sectionRefs.current.set(sectionId, el);
        else sectionRefs.current.delete(sectionId);
    }, []);
    useEffect(()=>{
        const container = scrollContainerRef.current;
        if (!container || sections.length === 0) return;
        // A deep link selects the assignment context. Initial observer delivery
        // must not replace it with whichever section was visible before scrolling.
        let userScrolling = scrollNavigation.current === sectionId;
        scrollNavigation.current = null;
        if (!userScrolling && sectionId) {
            sectionRefs.current.get(sectionId)?.scrollIntoView({
                block: 'start'
            });
        }
        const enableScrollSync = ()=>{
            userScrolling = true;
        };
        const inputEvents = [
            'wheel',
            'touchmove',
            'keydown',
            'pointerdown'
        ];
        for (const event of inputEvents)container.addEventListener(event, enableScrollSync);
        const observer = new IntersectionObserver((entries)=>{
            if (!userScrolling) return;
            // Find the topmost visible section.
            let topEntry = null;
            for (const entry of entries){
                if (entry.isIntersecting) {
                    if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
                        topEntry = entry;
                    }
                }
            }
            if (topEntry) {
                const secId = topEntry.target.getAttribute('data-section-id');
                if (secId && secId !== sectionId && id && volumeId) {
                    // Update URL without full navigation (just replace the sectionId param).
                    scrollNavigation.current = secId;
                    navigate(`/proposal/${id}/v2/volume/${volumeId}/section/${secId}`, {
                        replace: true
                    });
                }
            }
        }, {
            root: container,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0
        });
        for (const el of sectionRefs.current.values()){
            observer.observe(el);
        }
        return ()=>{
            observer.disconnect();
            for (const event of inputEvents)container.removeEventListener(event, enableScrollSync);
        };
    }, [
        sections,
        sectionId,
        id,
        volumeId,
        navigate
    ]);
    // No sections → full metadata landing
    if (!outline.isLoading && sections.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full flex-col bg-slate-50",
            children: /*#__PURE__*/ _jsx("div", {
                className: "flex-1 overflow-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "mx-auto w-full max-w-3xl p-4 sm:p-6 lg:p-8",
                    children: [
                        /*#__PURE__*/ _jsx(VolumeMetadataPanel, {
                            projectId: projectId,
                            volume: volume
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-6 flex items-center justify-center",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "max-w-md rounded-xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center",
                                children: [
                                    /*#__PURE__*/ _jsx(FileText, {
                                        className: "mx-auto h-8 w-8 text-slate-300",
                                        "aria-hidden": "true"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-3 text-sm font-medium text-slate-700",
                                        children: "No sections yet"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-1 text-xs text-slate-500",
                                        children: [
                                            "Click ",
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-semibold text-slate-700",
                                                children: "Add"
                                            }),
                                            " in the sidebar to create the first section."
                                        ]
                                    })
                                ]
                            })
                        })
                    ]
                })
            })
        });
    }
    const pageLimit = meta.pageLimits.find((p)=>p.isCurrentVersion) ?? meta.pageLimits[0];
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col bg-slate-200/60",
        children: [
            /*#__PURE__*/ _jsx(FormatBar, {
                projectId: projectId,
                volume: volume,
                meta: meta,
                pageLimit: pageLimit,
                formatStyle: formatStyle,
                showSettings: showSettings,
                onToggleSettings: ()=>setShowSettings((v)=>!v),
                formatOverrides: formatOverrides,
                onFormatChange: setFormatOverrides
            }),
            activeEditor && /*#__PURE__*/ _jsx("div", {
                className: "sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur-sm shadow-sm",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "mx-auto",
                    style: {
                        maxWidth: formatStyle.pageWidthPx
                    },
                    children: /*#__PURE__*/ _jsx(EditorToolbar, {
                        editor: activeEditor
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: scrollContainerRef,
                className: "flex-1 overflow-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "mx-auto w-full py-8 px-4",
                    style: {
                        maxWidth: formatStyle.pageWidthPx + 48
                    },
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            ref: pageRef,
                            className: "bg-white shadow-md rounded-sm relative",
                            style: {
                                padding: `${formatStyle.marginTopPx}px ${formatStyle.marginRightPx}px ${formatStyle.marginBottomPx}px ${formatStyle.marginLeftPx}px`,
                                fontFamily: formatStyle.fontFamily,
                                fontSize: `${formatStyle.fontSizePt}pt`,
                                lineHeight: formatStyle.lineHeight,
                                minHeight: formatStyle.pageHeightPx,
                                // Repeating background: faint dashed line every pageHeight px
                                backgroundImage: `repeating-linear-gradient(
                to bottom,
                transparent 0px,
                transparent ${formatStyle.pageHeightPx - 1}px,
                #e2e8f0 ${formatStyle.pageHeightPx - 1}px,
                #e2e8f0 ${formatStyle.pageHeightPx}px
              )`,
                                backgroundSize: `100% ${formatStyle.pageHeightPx}px`
                            },
                            children: [
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "font-bold text-slate-900 mb-4",
                                    style: {
                                        fontSize: `${formatStyle.h1SizePt}pt`
                                    },
                                    children: volume.displayName
                                }),
                                updateSection.isError && /*#__PURE__*/ _jsx("p", {
                                    role: "alert",
                                    children: "Unable to save the section name. Try again."
                                }),
                                sections.map((s, i)=>{
                                    const headingSizePt = s.depth === 0 ? formatStyle.h2SizePt : s.depth === 1 ? formatStyle.h3SizePt : formatStyle.bodySizePt + 1;
                                    return /*#__PURE__*/ _jsxs("div", {
                                        ref: (el)=>registerRef(s.sectionId, el),
                                        "data-section-id": s.sectionId,
                                        id: `section-${s.sectionId}`,
                                        className: `${sectionId === s.sectionId ? 'bg-blue-50/30' : ''} transition-colors duration-300`,
                                        style: {
                                            scrollMarginTop: '80px'
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(EditableHeading, {
                                                title: s.title,
                                                sizePt: headingSizePt,
                                                depth: s.depth,
                                                isFirst: i === 0,
                                                onRename: (newTitle)=>{
                                                    updateSection.mutate({
                                                        sectionId: s.sectionId,
                                                        updates: {
                                                            title: newTitle
                                                        }
                                                    });
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "relative pt-2",
                                                style: {
                                                    paddingLeft: s.depth > 0 ? `${s.depth * 16}px` : undefined
                                                },
                                                children: /*#__PURE__*/ _jsx(SectionEditor, {
                                                    projectId: projectId,
                                                    sectionId: s.sectionId,
                                                    bare: true,
                                                    onFocus: (ed)=>setActiveEditor(ed)
                                                }, s.sectionId)
                                            })
                                        ]
                                    }, s.sectionId);
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(PageIndicator, {
                            pageRef: pageRef,
                            pageHeightPx: formatStyle.pageHeightPx,
                            pageLimit: pageLimit,
                            sectionCount: sections.length,
                            onPageCount: setPageCount
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mx-auto mt-2 mb-8 text-center text-xs text-slate-400",
                            children: [
                                pageCount,
                                " page",
                                pageCount === 1 ? '' : 's',
                                pageLimit ? ` · Limit: ${formatPageLimitChip(pageLimit)}` : ''
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Editable Heading — click to rename section title inline
// ---------------------------------------------------------------------------
function EditableHeading({ title, sizePt, depth, isFirst, onRename }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(title);
    const save = ()=>{
        const trimmed = value.trim();
        if (trimmed && trimmed !== title) {
            onRename(trimmed);
        } else {
            setValue(title); // revert
        }
        setEditing(false);
    };
    // Sync when title changes externally.
    useEffect(()=>{
        setValue(title);
    }, [
        title
    ]);
    if (editing) {
        return /*#__PURE__*/ _jsx("input", {
            type: "text",
            value: value,
            onChange: (e)=>setValue(e.target.value),
            onBlur: save,
            onKeyDown: (e)=>{
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') {
                    setValue(title);
                    setEditing(false);
                }
            },
            ref: (el)=>el?.focus(),
            className: "w-full bg-transparent font-bold text-slate-900 outline-none border-b-2 border-blue-400",
            style: {
                fontSize: `${sizePt}pt`,
                marginTop: isFirst ? '0' : depth === 0 ? '1.5em' : '1em',
                marginBottom: '0.3em',
                paddingLeft: depth > 0 ? `${depth * 16}px` : undefined
            }
        });
    }
    return /*#__PURE__*/ _jsxs("h2", {
        className: "group font-bold text-slate-900 cursor-text",
        style: {
            fontSize: `${sizePt}pt`,
            marginTop: isFirst ? '0' : depth === 0 ? '1.5em' : '1em',
            marginBottom: '0.3em',
            paddingLeft: depth > 0 ? `${depth * 16}px` : undefined
        },
        onClick: ()=>setEditing(true),
        title: "Click to rename",
        children: [
            title || '(untitled)',
            /*#__PURE__*/ _jsx("span", {
                className: "ml-2 text-[10px] font-normal text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity",
                children: "click to edit"
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Page Indicator — measures rendered height, shows page count vs limit
// ---------------------------------------------------------------------------
function PageIndicator({ pageRef, pageHeightPx, pageLimit, sectionCount, onPageCount }) {
    const [pages, setPages] = useState(1);
    useEffect(()=>{
        const el = pageRef.current;
        if (!el) return;
        const measure = ()=>{
            const height = el.scrollHeight;
            const count = Math.max(1, Math.ceil(height / pageHeightPx));
            setPages(count);
            onPageCount(count);
        };
        measure();
        const resizeObs = new ResizeObserver(measure);
        resizeObs.observe(el);
        return ()=>resizeObs.disconnect();
    }, [
        pageRef,
        pageHeightPx,
        onPageCount
    ]);
    const totalAllowed = pageLimit ? effectivePageTotal(pageLimit) : undefined;
    const overLimit = totalAllowed != null && pages > totalAllowed;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto mt-4 flex items-center justify-center gap-3 text-xs",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: overLimit ? 'font-semibold text-rose-600' : 'text-slate-500',
                        children: [
                            pages,
                            " / ",
                            totalAllowed ?? '?',
                            " pages"
                        ]
                    }),
                    totalAllowed != null && /*#__PURE__*/ _jsx("div", {
                        className: "h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: `h-full rounded-full transition-all ${overLimit ? 'bg-rose-500' : pages / totalAllowed > 0.8 ? 'bg-amber-400' : 'bg-emerald-400'}`,
                            style: {
                                width: `${Math.min(100, pages / totalAllowed * 100)}%`
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "text-slate-400",
                children: [
                    sectionCount,
                    " section",
                    sectionCount === 1 ? '' : 's'
                ]
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Format Bar — volume info + format preset chips + settings popover
// ---------------------------------------------------------------------------
function FormatBar({ projectId, volume, meta, pageLimit, formatStyle, showSettings, onToggleSettings, formatOverrides, onFormatChange }) {
    const [showMeta, setShowMeta] = useState(false);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex-shrink-0 border-b border-slate-200 bg-white",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 px-3 py-2 sm:px-4",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>setShowMeta((v)=>!v),
                        className: "inline-flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-slate-50",
                        children: [
                            /*#__PURE__*/ _jsx(BookOpen, {
                                className: "h-3.5 w-3.5 flex-shrink-0 text-slate-400",
                                "aria-hidden": "true"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "min-w-0 truncate text-sm font-medium text-slate-800",
                                children: volume.displayName
                            }),
                            showMeta ? /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-3 w-3 flex-shrink-0 text-slate-400"
                            }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                className: "h-3 w-3 flex-shrink-0 text-slate-400"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "ml-auto flex items-center gap-1.5",
                        children: [
                            meta.formatRules.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                className: "hidden items-center gap-1 text-[10px] text-blue-600 xs:flex",
                                children: [
                                    /*#__PURE__*/ _jsx(Info, {
                                        className: "h-3 w-3"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "hidden sm:inline",
                                        children: "From solicitation"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: onToggleSettings,
                                className: `rounded-md p-1.5 transition-colors ${showSettings ? 'bg-slate-200 text-slate-900' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`,
                                title: "Format settings",
                                children: /*#__PURE__*/ _jsx(Settings2, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-1.5 border-t border-slate-50 px-3 py-1.5 sm:px-4",
                children: [
                    /*#__PURE__*/ _jsx(FormatChip, {
                        children: formatStyle.fontFamily.split(',')[0]
                    }),
                    /*#__PURE__*/ _jsxs(FormatChip, {
                        children: [
                            formatStyle.fontSizePt,
                            "pt"
                        ]
                    }),
                    formatStyle.lineSpacingLabel && /*#__PURE__*/ _jsx(FormatChip, {
                        children: formatStyle.lineSpacingLabel
                    }),
                    formatStyle.marginLabel && /*#__PURE__*/ _jsx(FormatChip, {
                        children: formatStyle.marginLabel
                    }),
                    pageLimit && /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800",
                        children: [
                            /*#__PURE__*/ _jsx(Ruler, {
                                className: "h-2.5 w-2.5",
                                "aria-hidden": "true"
                            }),
                            formatPageLimitChip(pageLimit)
                        ]
                    }),
                    volume.dueDate && /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1 text-[11px] text-slate-500",
                        children: [
                            /*#__PURE__*/ _jsx(Calendar, {
                                className: "h-3 w-3",
                                "aria-hidden": "true"
                            }),
                            volume.dueDate
                        ]
                    })
                ]
            }),
            showSettings && /*#__PURE__*/ _jsx(FormatSettingsPanel, {
                formatStyle: formatStyle,
                overrides: formatOverrides,
                onChange: onFormatChange,
                hasRules: meta.formatRules.length > 0
            }),
            showMeta && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-slate-100 bg-slate-50/50 px-3 py-4 sm:px-4",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "mx-auto max-w-3xl",
                    children: /*#__PURE__*/ _jsx(VolumeMetadataPanel, {
                        projectId: projectId,
                        volume: volume
                    })
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Format Settings Panel — edit font, size, spacing, margins
// ---------------------------------------------------------------------------
function FormatSettingsPanel({ formatStyle, overrides, onChange, hasRules }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "border-t border-slate-100 bg-slate-50 px-3 py-3 sm:px-4",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "mx-auto flex max-w-3xl flex-wrap items-end gap-3 sm:gap-4",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1",
                            children: "Font"
                        }),
                        /*#__PURE__*/ _jsx("select", {
                            value: formatStyle.fontFamily,
                            onChange: (e)=>onChange({
                                    ...overrides,
                                    fontFamily: e.target.value
                                }),
                            className: "rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800",
                            children: FONT_OPTIONS.map((f)=>/*#__PURE__*/ _jsx("option", {
                                    value: f.value,
                                    style: {
                                        fontFamily: f.value
                                    },
                                    children: f.label
                                }, f.value))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1",
                            children: "Size"
                        }),
                        /*#__PURE__*/ _jsx("select", {
                            value: formatStyle.fontSizePt,
                            onChange: (e)=>{
                                const pt = Number.parseInt(e.target.value, 10);
                                onChange({
                                    ...overrides,
                                    fontSizePt: pt,
                                    bodySizePt: pt,
                                    h1SizePt: Math.round(pt * 1.6),
                                    h2SizePt: Math.round(pt * 1.35),
                                    h3SizePt: Math.round(pt * 1.15)
                                });
                            },
                            className: "rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800",
                            children: [
                                8,
                                9,
                                10,
                                11,
                                12,
                                13,
                                14,
                                16,
                                18,
                                20,
                                24
                            ].map((pt)=>/*#__PURE__*/ _jsxs("option", {
                                    value: pt,
                                    children: [
                                        pt,
                                        "pt"
                                    ]
                                }, pt))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1",
                            children: "Spacing"
                        }),
                        /*#__PURE__*/ _jsxs("select", {
                            value: formatStyle.lineHeight,
                            onChange: (e)=>{
                                const val = e.target.value;
                                const labels = {
                                    '1.15': 'Single',
                                    '1.5': '1.5 spacing',
                                    '2.0': 'Double'
                                };
                                onChange({
                                    ...overrides,
                                    lineHeight: val,
                                    lineSpacingLabel: labels[val] || `${val}×`
                                });
                            },
                            className: "rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800",
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "1.15",
                                    children: "Single"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "1.5",
                                    children: "1.5"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "2.0",
                                    children: "Double"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1",
                            children: "Margins"
                        }),
                        /*#__PURE__*/ _jsxs("select", {
                            value: formatStyle.marginLeftPx,
                            onChange: (e)=>{
                                const px = Number.parseInt(e.target.value, 10);
                                const inches = px / INCH_PX;
                                onChange({
                                    ...overrides,
                                    marginTopPx: px,
                                    marginBottomPx: px,
                                    marginLeftPx: px,
                                    marginRightPx: px,
                                    marginLabel: `${inches}" margins`
                                });
                            },
                            className: "rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800",
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: 48,
                                    children: '0.5"'
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: 72,
                                    children: '0.75"'
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: 96,
                                    children: '1"'
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: 120,
                                    children: '1.25"'
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: 144,
                                    children: '1.5"'
                                })
                            ]
                        })
                    ]
                }),
                hasRules && /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    onClick: ()=>onChange({}),
                    className: "text-[10px] font-medium text-blue-600 hover:text-blue-800",
                    children: "Reset to solicitation defaults"
                })
            ]
        })
    });
}
function FormatChip({ children }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rounded border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium text-slate-600",
        children: children
    });
}
/** US Letter at 96 DPI */ const LETTER_WIDTH_PX = 816;
const LETTER_HEIGHT_PX = 1056;
const INCH_PX = 96;
function deriveFormatStyle(meta) {
    let fontFamily = 'Times New Roman, serif';
    let fontSizePt = 12;
    let lineHeight = '1.5';
    let lineSpacingLabel = '';
    let marginInches = 1;
    let marginLabel = '1" margins';
    for (const rule of meta.formatRules){
        const rt = (rule.ruleType || '').toLowerCase();
        const val = (rule.value || '').trim();
        if (rt === 'font' && val) {
            const fontName = val.replace(/\d+[\s-]?pt/gi, '').replace(/[,;].*/g, '').trim();
            if (fontName) fontFamily = `${fontName}, serif`;
        }
        if (rt === 'font_size' && val) {
            const match = val.match(/(\d+)/);
            if (match) fontSizePt = Number.parseInt(match[1], 10);
        }
        if (rt === 'line_spacing' && val) {
            const lower = val.toLowerCase();
            if (lower.includes('single')) {
                lineHeight = '1.15';
                lineSpacingLabel = 'Single';
            } else if (lower.includes('1.5')) {
                lineHeight = '1.5';
                lineSpacingLabel = '1.5 spacing';
            } else if (lower.includes('double')) {
                lineHeight = '2.0';
                lineSpacingLabel = 'Double';
            } else {
                const num = Number.parseFloat(val);
                if (!Number.isNaN(num) && num > 0 && num < 5) {
                    lineHeight = String(num);
                    lineSpacingLabel = `${num}× spacing`;
                }
            }
        }
        if (rt === 'margin' && val) {
            const match = val.match(/([\d.]+)\s*(?:inch|in|")/i);
            if (match) {
                marginInches = Number.parseFloat(match[1]);
                marginLabel = `${marginInches}" margins`;
            }
        }
    }
    const marginPx = Math.round(marginInches * INCH_PX);
    return {
        fontFamily,
        fontSizePt,
        bodySizePt: fontSizePt,
        h1SizePt: Math.round(fontSizePt * 1.6),
        h2SizePt: Math.round(fontSizePt * 1.35),
        h3SizePt: Math.round(fontSizePt * 1.15),
        lineHeight,
        lineSpacingLabel,
        marginTopPx: marginPx,
        marginBottomPx: marginPx,
        marginLeftPx: marginPx,
        marginRightPx: marginPx,
        marginLabel,
        pageWidthPx: LETTER_WIDTH_PX,
        pageHeightPx: LETTER_HEIGHT_PX
    };
}
function formatPageLimitChip(p) {
    const unit = p.unit || 'pages';
    const base = `${p.limit} ${unit}`;
    if (p.scope === 'per_instance' && p.instanceLabel) {
        if (p.instanceMax && p.instanceMax > 0) {
            return `${base} × ${p.instanceMax} ${pluralize(p.instanceLabel, p.instanceMax)}`;
        }
        return `${base} per ${p.instanceLabel}`;
    }
    // A subsection scope of "other" is the extractor saying it could not name
    // the scope; the limit then reads as the volume's.
    if (p.scope === 'per_subsection' && p.subsectionScope && p.subsectionScope.toLowerCase() !== 'other') {
        if (p.instanceMax && p.instanceMax > 0) {
            return `${base} per ${p.subsectionScope} · max ${p.instanceMax}`;
        }
        return `${base} per ${p.subsectionScope}`;
    }
    return base;
}
/** Effective total page count for a page limit (for progress bar comparison). */ function effectivePageTotal(p) {
    if (p.scope === 'per_instance' && p.instanceMax && p.instanceMax > 0) {
        return p.limit * p.instanceMax;
    }
    return p.limit;
}
function pluralize(word, count) {
    if (count === 1) return word;
    const lower = word.toLowerCase();
    if (/[bcdfghjklmnpqrstvwxz]y$/.test(lower)) return `${word.slice(0, -1)}ies`;
    if (/(s|x|z|ch|sh)$/.test(lower)) return `${word}es`;
    return `${word}s`;
}
function flattenAll(sections, depth = 0) {
    const out = [];
    for (const s of sections){
        out.push({
            sectionId: s.sectionId,
            title: s.title,
            depth
        });
        if (s.children && Array.isArray(s.children)) {
            out.push(...flattenAll(s.children, depth + 1));
        }
    }
    return out;
}
